import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@bi-studio/shared/hooks";
import {
  LoadingOverlay,
} from "@mantine/core";
import { LoadingScreen, ErrorState } from "@bi-studio/shared/components";
import {
  createFolder,
  updateFolder,
  addFolderItem,
  deleteFolder,
  deleteReport,
  deleteChart,
  deleteDashboard,
  deleteForm,
  getFolderForItem,
  deleteTabGroup,
} from "@bi-studio/shared/apis";
import { type SelectedItem } from "./FolderActionToolbar";
import { MoveItemsModal } from "./modals/MoveItemsModal";
import { CreateFolderModal } from "./modals/CreateFolderModal";
import { DeleteConfirmModal } from "./modals/DeleteConfirmModal";
import { RootFolderGrid } from "./RootFolderGrid";
import { PageActions, OpenPlaygroundButton, AddNewEntityButton, PageContainer, ItemDetailModal } from "@/components";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  fetchRootContent,
  selectTopLevelFolders,
  selectRootReports,
  selectRootDashboards,
  selectRootCharts,
  selectRootForms,
  selectRootTabGroups,
  selectRootLoading,
  selectRootLoaded,
  selectFolderError,
} from "@/store/slices/folderSlice";

export function RootFolderView() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Redux states
  const topLevelFolders = useAppSelector(selectTopLevelFolders);
  const rootReports = useAppSelector(selectRootReports);
  const rootDashboards = useAppSelector(selectRootDashboards);
  const rootCharts = useAppSelector(selectRootCharts);
  const rootForms = useAppSelector(selectRootForms);
  const rootTabGroups = useAppSelector(selectRootTabGroups);
  const isLoading = useAppSelector(selectRootLoading);
  const isLoaded = useAppSelector(selectRootLoaded);
  const error = useAppSelector(selectFolderError);

  const [itemToMove, setItemToMove] = React.useState<SelectedItem | null>(null);
  const [itemToDelete, setItemToDelete] = React.useState<SelectedItem | null>(null);
  const [detailItem, setDetailItem] = React.useState<SelectedItem | null>(null);
  const [showCreateFolderModal, setShowCreateFolderModal] = React.useState(false);
  const [isCreatingFolder, setIsCreatingFolder] = React.useState(false);



  React.useEffect(() => {
    if (!token) return;

    if (!isLoaded && !isLoading) {
      dispatch(fetchRootContent(token));
    }

    // Listen for refresh events
    const handleRefresh = () => {
      dispatch(fetchRootContent(token));
    };
    window.addEventListener("refresh-folders", handleRefresh);

    return () => {
      window.removeEventListener("refresh-folders", handleRefresh);
    };
  }, [token, dispatch]);





  const handleCreateFolder = async (folderName: string) => {
    if (!token) return;

    try {
      setIsCreatingFolder(true);
      await createFolder(token, { name: folderName, parentFolderId: null });
      // Reload folders
      window.dispatchEvent(new CustomEvent("refresh-folders"));
      dispatch(fetchRootContent(token));
    } finally {
      setIsCreatingFolder(false);
    }
  };

  const handleItemClick = (
    type: "report" | "dashboard" | "chart" | "form" | "tab_group" | "folder",
    id: string
  ) => {
    if (type === "report") {
      navigate(`/reports/${id}`);
    } else if (type === "chart") {
      navigate(`/charts/${id}`);
    } else if (type === "form") {
      navigate(`/forms/${id}`);
    } else if (type === "tab_group") {
      navigate(`/tab-groups/${id}`);
    } else if (type === "folder") {
      navigate(`/folders/${id}`);
    } else {
      navigate(`/dashboards/${id}`);
    }
  };

  const handleMove = async (targetFolderId: string | null) => {
    if (!token || !itemToMove) return;

    try {
      if (itemToMove.type === "folder") {
        // Move folder by updating parentFolderId
        await updateFolder(token, itemToMove.id, { parentFolderId: targetFolderId });
      } else {
        // For items, we need to:
        // 1. Remove from root (no folder association means it's in root)
        // 2. Add to target folder (if not root)

        // Items in root don't have FolderItem entries, so we just add to target
        if (targetFolderId) {
          await addFolderItem(token, targetFolderId, {
            itemType: itemToMove.type,
            itemId: itemToMove.id,
          });
        }
        // If moving to root, we don't need to do anything (items without FolderItem are in root)
      }
    } catch (err) {
      alert(`Failed to move: ${err instanceof Error ? err.message : "Failed"}`);
    }

    // Refresh
    setItemToMove(null);
    window.dispatchEvent(new CustomEvent("refresh-folders"));
    dispatch(fetchRootContent(token));
  };

  const handleDelete = async () => {
    if (!token || !itemToDelete) return;

    let deletedDashboardFolderId: string | null = null;

    try {
      if (itemToDelete.type === "folder") {
        await deleteFolder(token, itemToDelete.id);
      } else if (itemToDelete.type === "report") {
        await deleteReport(token, itemToDelete.id);
      } else if (itemToDelete.type === "chart") {
        await deleteChart(token, itemToDelete.id);
      } else if (itemToDelete.type === "dashboard") {
        const folderInfo = await getFolderForItem(token, "dashboard", itemToDelete.id);
        deletedDashboardFolderId = folderInfo.folderId;
        await deleteDashboard(token, itemToDelete.id);
      } else if (itemToDelete.type === "form") {
        await deleteForm(token, itemToDelete.id);
      } else if (itemToDelete.type === "tab_group") {
        await deleteTabGroup(token, itemToDelete.id);
      }
    } catch (err) {
      alert(`Failed to delete: ${err instanceof Error ? err.message : "Failed"}`);
    }

    // Refresh
    setItemToDelete(null);
    window.dispatchEvent(new CustomEvent("refresh-folders"));
    dispatch(fetchRootContent(token));

    // Navigate to folder if a dashboard was deleted and it was in a folder
    if (itemToDelete.type === "dashboard" && deletedDashboardFolderId) {
      navigate(`/folders/${deletedDashboardFolderId}`);
    }
  };

  if (error && !isLoading) {
    return (
      <ErrorState
        message={error}
        onAction={() => {
          if (token) {
            dispatch(fetchRootContent(token));
          }
        }}
        actionLabel="Retry"
      />
    );
  }

  const isReady = topLevelFolders.length > 0 || rootReports.length > 0 || rootDashboards.length > 0 || rootCharts.length > 0 || rootForms.length > 0 || rootTabGroups.length > 0;

  if (!isReady && isLoading) {
    return <LoadingScreen message="Loading workspace..." />;
  }

  return (
    <PageContainer>
      <LoadingOverlay visible={isLoading && isReady} />
      <PageActions>
        <OpenPlaygroundButton onClick={() => navigate("/playground")} />
        <AddNewEntityButton folderId="root" onNewFolder={() => setShowCreateFolderModal(true)} />
      </PageActions>

      <RootFolderGrid
        topLevelFolders={topLevelFolders}
        rootDashboards={rootDashboards}
        rootCharts={rootCharts}
        rootReports={rootReports}
        rootForms={rootForms}
        rootTabGroups={rootTabGroups}
        onMove={(type, id) => setItemToMove({ type: type as any, id })}
        onDelete={(type, id) => setItemToDelete({ type: type as any, id })}
        onViewDetails={(type, id) => setDetailItem({ type: type as any, id })}
        onItemClick={handleItemClick}
      />

      <CreateFolderModal
        opened={showCreateFolderModal}
        onClose={() => setShowCreateFolderModal(false)}
        onSubmit={handleCreateFolder}
        loading={isCreatingFolder}
      />

      {
        itemToMove && (
          <MoveItemsModal
            selectedItems={[itemToMove]}
            currentFolderId={null}
            onMove={handleMove}
            onClose={() => setItemToMove(null)}
          />
        )
      }

      <DeleteConfirmModal
        opened={itemToDelete !== null}
        onClose={() => setItemToDelete(null)}
        onDelete={handleDelete}
        selectedCount={itemToDelete ? 1 : 0}
      />



      <ItemDetailModal
        opened={detailItem !== null}
        onClose={() => setDetailItem(null)}
        itemType={detailItem?.type as any}
        itemId={detailItem?.id ?? null}
      />
    </PageContainer>
  );
}
